/**
 * Shared CVA variant definitions used across primitive components
 * Centralizes common patterns for consistency and maintainability
 */

/**
 * Text size scale variants
 * Maps component scales to design system text sizes
 * Used in: Avatar, Badge, Chip, Button, Label, etc.
 */
export const textScaleVariants = {
  s: 'text-[length:var(--text-size--2)]', // 12px
  m: 'text-[length:var(--text-size--1)]', // 14px
  l: 'text-[length:var(--text-size-0)]', // 16px
  xl: 'text-[length:var(--text-size-1)]', // 18px
} as const;

/**
 * Extended text size scale variants (for special cases)
 * Used when components need smaller text sizes
 */
export const textScaleExtendedVariants = {
  xs: 'text-[length:var(--text-size--3)]', // 11px
  s: 'text-[length:var(--text-size--2)]', // 12px
  m: 'text-[length:var(--text-size--1)]', // 14px
  l: 'text-[length:var(--text-size-0)]', // 16px
  xl: 'text-[length:var(--text-size-1)]', // 18px
} as const;

/**
 * Component size scale variants
 * Standard sizing for circular/square components
 * Used in: Avatar, Badge (icon), Checkbox, Radio, etc.
 */
export const componentSizeVariants = {
  s: 'size-6', // 24px
  m: 'size-8', // 32px
  l: 'size-11', // 44px
} as const;

/**
 * Size variants for width and height simultaneously
 * Used for square elements
 */
export const sizeVariants = {
  3: 'size-3',
  4: 'size-4',
  5: 'size-5',
  6: 'size-6',
  8: 'size-8',
  11: 'size-11',
  12: 'size-12',
  16: 'size-16',
} as const;

/**
 * Icon size scale variants
 * Standard icon sizing within components
 * Used in: Avatar, Button, Alert, etc.
 */
export const iconSizeVariants = {
  s: 'size-3', // 12px
  m: 'size-4', // 16px
  l: 'size-5', // 20px
} as const;

/**
 * Badge/Avatar background color variants
 * Light backgrounds with dark mode support for colored badges
 * Used in: Avatar, Badge, Chip, Status, etc.
 */
export const coloredBackgroundVariants = {
  blue: 'bg-blue-100 dark:bg-blue-900/20',
  teal: 'bg-teal-100 dark:bg-teal-900/20',
  red: 'bg-red-100 dark:bg-red-900/20',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/20',
  green: 'bg-green-100 dark:bg-green-900/20',
  purple: 'bg-purple-100 dark:bg-purple-900/20',
  orange: 'bg-orange-100 dark:bg-orange-900/20',
  pink: 'bg-pink-100 dark:bg-pink-900/20',
} as const;

/**
 * Text color for colored backgrounds
 * Ensures proper contrast on colored backgrounds
 * Used in: Avatar, Badge, Chip, Status, etc.
 */
export const coloredTextVariants = {
  blue: 'text-blue-900 dark:text-blue-100',
  teal: 'text-teal-900 dark:text-teal-100',
  red: 'text-red-900 dark:text-red-100',
  yellow: 'text-yellow-900 dark:text-yellow-100',
  green: 'text-green-900 dark:text-green-100',
  purple: 'text-purple-900 dark:text-purple-100',
  orange: 'text-orange-900 dark:text-orange-100',
  pink: 'text-pink-900 dark:text-pink-100',
} as const;

/**
 * Semantic text color variants
 * Maps to Nabat design system text colors
 * Used in: Typography, Label, Button, Link, etc.
 */
export const textColorVariants = {
  primary: 'text-foreground',
  secondary: 'text-foreground-secondary',
  tertiary: 'text-foreground-muted',
  brand: 'text-brand',
  link: 'text-foreground-link',
  success: 'text-status-success',
  warning: 'text-status-warning',
  danger: 'text-status-danger',
  info: 'text-status-info',
} as const;

/**
 * Spacing scale variants for padding
 * Used in: Button, Badge, Chip, Input, etc.
 */
export const paddingScaleVariants = {
  s: 'px-sm py-xs',
  m: 'px-md py-sm',
  l: 'px-lg py-md',
  xl: 'px-xl py-lg',
} as const;

/**
 * Border radius variants
 * Maps to design system border radius tokens
 * Used in: Button, Badge, Input, Card, etc.
 */
export const borderRadiusVariants = {
  none: 'rounded-none',
  xs: 'rounded-xs',
  sm: 'rounded-sm',
  md: 'rounded',
  lg: 'rounded-sm',
  pill: 'rounded-pill',
  full: 'rounded-full',
} as const;

/**
 * Common disabled state classes
 * Used across all interactive components
 */
export const disabledStateClasses =
  'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed' as const;

/**
 * Common focus ring classes
 * Used across all interactive components
 */
export const focusRingClasses =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2' as const;

/**
 * A 24px-tall hit area for a text link, centred on its line, without moving the text.
 * The launch build rules ask for it on every text link, inline ones included.
 */
export const linkHitAreaClasses =
  "relative after:absolute after:inset-x-0 after:top-1/2 after:h-6 after:-translate-y-1/2 after:content-['']" as const;

/**
 * Common transition classes
 * Used for smooth state changes
 */
export const transitionClasses = 'transition-colors duration-200' as const;

/**
 * Input/button height variants
 * Used for input fields, text fields, and similar interactive elements
 * Maps to component scale (s/m/l)
 */
export const inputHeightVariants = {
  s: 'h-11',
  m: 'h-14',
  l: 'h-18',
} as const;

/**
 * Button/action height variants
 * Used specifically for button and action components
 * Maps to component scale (s/m/l)
 */
export const buttonHeightVariants = {
  s: 'h-8',
  compact: 'h-10', // 40px, the map screens' button height
  m: 'h-12',
  l: 'h-16',
} as const;

/**
 * Chip/badge height variants
 * Used for chips, badges, and pill-shaped elements
 * Maps to component scale (s/m/l)
 */
export const chipHeightVariants = {
  s: 'h-6',
  m: 'h-8',
  l: 'h-10',
} as const;

/**
 * Status/semantic color variants for backgrounds
 * Used for status indicators, alerts, and semantic UI elements
 */
export const statusBackgroundVariants = {
  brand: 'bg-brand',
  info: 'bg-status-info',
  success: 'bg-status-success',
  warning: 'bg-status-warning',
  danger: 'bg-status-danger',
} as const;

/**
 * Status/semantic color variants for text
 * Used for status indicators, alerts, and semantic UI elements
 */
export const statusTextVariants = {
  brand: 'text-brand',
  info: 'text-status-info',
  success: 'text-status-success',
  warning: 'text-status-warning',
  danger: 'text-status-danger',
} as const;

/**
 * Status/semantic color variants for borders
 * Used for status indicators, alerts, and semantic UI elements
 */
export const statusBorderVariants = {
  brand: 'border-brand',
  info: 'border-status-info',
  success: 'border-status-success',
  warning: 'border-status-warning',
  danger: 'border-status-danger',
} as const;

/**
 * Gap scale variants
 * Used for spacing between flex/grid items
 */
export const gapScaleVariants = {
  xs: 'gap-xs',
  s: 'gap-sm',
  m: 'gap-md',
  l: 'gap-lg',
  xl: 'gap-xl',
} as const;

// ============================================================================
// TYPOGRAPHY-SPECIFIC VARIANTS
// ============================================================================

/**
 * Typography text size variants (complete design system scale)
 * Maps to CSS custom properties for all text sizes
 * Used primarily in: Typography component
 */
export const typographyTextSizes = {
  display: 'text-[length:var(--text-size-8)]', // 64px
  h1: 'text-[length:var(--text-size-7)]', // 56px
  h2: 'text-[length:var(--text-size-6)]', // 48px
  h3: 'text-[length:var(--text-size-5)]', // 40px
  h4: 'text-[length:var(--text-size-4)]', // 32px
  h5: 'text-[length:var(--text-size-3)]', // 26px
  h6: 'text-[length:var(--text-size-2)]', // 20px
  'body-large': 'text-[length:var(--text-size-1)]', // 18px
  body: 'text-[length:var(--text-size-0)]', // 16px (base)
  'body-small': 'text-[length:var(--text-size--1)]', // 14px
  caption: 'text-[length:var(--text-size--2)]', // 12px
  overline: 'text-[length:var(--text-size--3)]', // 11px
  label: 'text-[length:var(--text-size--1)]', // 14px
  code: 'text-[length:var(--text-size--1)]', // 14px
} as const;

/**
 * Line height (leading) variants
 * Used for different text styles and readability
 */
export const lineHeightVariants = {
  none: 'leading-none', // 1
  tight: 'leading-tight', // 1.25
  snug: 'leading-snug', // 1.375
  normal: 'leading-normal', // 1.5
  relaxed: 'leading-relaxed', // 1.625
  loose: 'leading-loose', // 2
  // Custom leading values
  '1.1': 'leading-[1.1]',
  '1.2': 'leading-[1.2]',
  '1.3': 'leading-[1.3]',
  '1.25': 'leading-[1.25]',
  '1.35': 'leading-[1.35]',
  '1.4': 'leading-[1.4]',
  '1.5': 'leading-[1.5]',
  '1.625': 'leading-[1.625]',
} as const;

/**
 * Font weight variants
 * Standard font weight scales
 */
export const fontWeightVariants = {
  light: 'font-light', // 300
  normal: 'font-normal', // 400
  medium: 'font-medium', // 500
  semibold: 'font-semibold', // 600
  bold: 'font-bold', // 700
} as const;

/**
 * Text alignment variants
 * Standard text alignment options
 */
export const textAlignVariants = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
} as const;

/**
 * Text transform variants
 * Used for case transformations
 */
export const textTransformVariants = {
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  capitalize: 'capitalize',
  normal: 'normal-case',
} as const;

/**
 * Letter spacing (tracking) variants
 * Used for text spacing adjustments
 */
export const letterSpacingVariants = {
  tighter: 'tracking-tighter', // -0.05em
  tight: 'tracking-tight', // -0.025em
  normal: 'tracking-normal', // 0
  wide: 'tracking-wide', // 0.025em
  wider: 'tracking-wider', // 0.05em
  widest: 'tracking-widest', // 0.1em
  overline: 'tracking-[0.2em]', // Custom for overline text
} as const;

/**
 * Font family variants
 * Used for different typeface styles
 */
export const fontFamilyVariants = {
  sans: 'font-sans',
  serif: 'font-serif',
  mono: 'font-mono',
} as const;

// ============================================================================
// COMMON UTILITY CLASSES
// ============================================================================

/**
 * Truncate text utility
 */
export const truncateClasses = 'truncate' as const;

/**
 * Select none utility (prevent text selection)
 */
export const selectNoneClasses = 'select-none' as const;

/**
 * Cursor variants
 */
export const cursorVariants = {
  pointer: 'cursor-pointer',
  notAllowed: 'cursor-not-allowed',
  default: 'cursor-default',
  move: 'cursor-move',
  text: 'cursor-text',
} as const;

/**
 * Opacity variants
 */
export const opacityVariants = {
  0: 'opacity-0',
  40: 'opacity-40',
  50: 'opacity-50',
  75: 'opacity-75',
  100: 'opacity-100',
} as const;

/**
 * Common background utilities
 */
export const backgroundUtilities = {
  transparent: 'bg-transparent',
} as const;

/**
 * Common border utilities
 */
export const borderUtilities = {
  none: 'border-none',
} as const;

/**
 * Common outline utilities
 */
export const outlineUtilities = {
  none: 'outline-none',
} as const;

/**
 * Pointer events utilities
 */
export const pointerEventsUtilities = {
  none: 'pointer-events-none',
  auto: 'pointer-events-auto',
} as const;

/**
 * Shadow variants
 * Standard shadow scales for elevation
 */
export const shadowVariants = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  '2xl': 'shadow-2xl',
} as const;

/**
 * Brand color utilities
 * Common brand-colored borders, rings, and text for interactive elements
 */
export const brandBorderVariants = {
  primary: 'border-brand-primary',
  hover: 'hover:border-brand-primary',
} as const;

export const brandRingVariants = {
  primary: 'ring-2 ring-brand-primary/20',
  focus: 'focus-visible:ring-2 focus-visible:ring-brand-primary',
} as const;

export const brandTextVariants = {
  primary: 'text-brand-primary',
  hover: 'hover:text-brand-primary',
} as const;

/**
 * Interactive state patterns
 * Common patterns for selectable/clickable items
 */
export const interactiveStateClasses = {
  selectable: 'cursor-pointer hover:border-brand-primary hover:shadow-sm',
  selected: 'border-brand-primary shadow-sm ring-2 ring-brand-primary/20',
  disabled: 'opacity-50 cursor-not-allowed pointer-events-none',
  clickable: 'cursor-pointer active:scale-[0.99]',
} as const;

/**
 * Transition/Animation utilities
 * Common transition patterns for smooth state changes
 */
export const transitionVariants = {
  default: 'transition-all duration-200',
  easeInOut: 'transition-all duration-200 ease-in-out',
  colors: 'transition-colors duration-200',
  opacity: 'transition-opacity duration-200',
  transform: 'transition-transform duration-200',
} as const;

/**
 * Typography pattern sets
 * Common typography combinations used across components
 */
export const typographyPatterns = {
  heading: 'font-inter font-semibold text-base leading-normal',
  bodyText: 'font-inter font-normal text-sm leading-normal',
  caption: 'font-inter font-normal text-xs leading-normal',
  buttonText: 'font-inter font-medium text-sm leading-normal',
} as const;

/**
 * Spacing scale variants for specific use cases
 * Complements gapScaleVariants with numeric gap values
 */
export const gapVariants = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
} as const;

/**
 * Padding variants for specific use cases
 */
export const paddingVariants = {
  0: 'p-0',
  1: 'p-1',
  2: 'p-2',
  3: 'p-3',
  4: 'p-4',
  5: 'p-5',
  6: 'p-6',
  8: 'p-8',
} as const;

/**
 * Width variants for common fixed widths
 */
export const widthVariants = {
  5: 'w-5',
  6: 'w-6',
  7: 'w-7',
  8: 'w-8',
  9: 'w-9',
  11: 'w-11',
  12: 'w-12',
  16: 'w-16',
  20: 'w-20',
  24: 'w-24',
  32: 'w-32',
  36: 'w-36',
  48: 'w-48',
  full: 'w-full',
} as const;

/**
 * Height variants for common fixed heights
 */
export const heightVariants = {
  4: 'h-4',
  5: 'h-5',
  6: 'h-6',
  8: 'h-8',
  11: 'h-11',
  12: 'h-12',
  16: 'h-16',
  20: 'h-20',
  24: 'h-24',
  32: 'h-32',
  full: 'h-full',
} as const;

/**
 * Border width variants
 */
export const borderWidthVariants = {
  0: 'border-0',
  1: 'border',
  2: 'border-2',
  4: 'border-4',
} as const;

/**
 * Positioning utility variants (for absolute/fixed positioning)
 */
export const insetVariants = {
  0: 'inset-0',
  1: 'top-1 right-1',
  2: 'top-2 right-2',
  3: 'top-3 right-3',
  4: 'top-4 right-4',
} as const;
